import * as Sentry from '@sentry/nextjs';
import 'client-only';

import { sentryConfig } from '../utils/sentry-config';

/**
 * Telemetry for the Clerk idle-tab recovery path.
 *
 * Tags every refresh attempt with `clerk_refresh.trigger`,
 * `clerk_refresh.outcome`, `clerk_refresh.duration_ms`, and (when known)
 * `tab.hidden_duration_ms`. Tags are written both to the new
 * `clerk.refresh` span AND to the active root span so any 401 errors
 * fired during the recovery window carry the same dimensions and can be
 * filtered together in dashboards / alerts.
 *
 * Every entry-point is wrapped in `try { … } catch {}` and short-circuits
 * when Sentry is disabled — observability must never interrupt the
 * recovery flow it observes.
 */

const SPAN_NAME = 'clerk.refresh';
const SPAN_OP = 'auth.refresh';
const BREADCRUMB_CATEGORY = 'clerk_refresh';

export const CLERK_REFRESH_ATTR = {
  TRIGGER: 'clerk_refresh.trigger',
  OUTCOME: 'clerk_refresh.outcome',
  DURATION_MS: 'clerk_refresh.duration_ms',
  CONSECUTIVE_FAILURES: 'clerk_refresh.consecutive_failures',
  HIDDEN_DURATION_MS: 'tab.hidden_duration_ms',
  SOURCE_KEY: 'clerk_refresh.source_key',
  INDICATOR_STATE: 'clerk_refresh.indicator_state',
} as const;

export const CLERK_REFRESH_MESSAGE = {
  HARD_RELOAD: 'clerk.refresh.hard_reload',
  INDICATOR_SHOWN: 'clerk.refresh.indicator_shown',
} as const;

export type ClerkRefreshTrigger =
  | 'visibilitychange'
  | 'pageshow'
  | 'online'
  | '401_query'
  | '401_mutation'
  | 'stay_signed_in'
  | 'document-engine-auth-failed'
  | 'unknown';

export type ClerkRefreshOutcomeStatus =
  | 'refreshed'
  | 'no-session'
  | 'timed-out'
  | 'offline'
  | 'failed';

export type ClerkRefreshIndicatorVisibleState = 'refreshing' | 'failed';

interface ObserveAttemptArgs {
  trigger: ClerkRefreshTrigger;
  /** Only set when the trigger relates to a tab returning to foreground. */
  hiddenDurationMs?: number;
  /**
   * First segment of the failing queryKey/mutationKey when the trigger is
   * a 401 from the TanStack cache handlers. Lets dashboards group "which
   * features keep stranding the session".
   */
  sourceKey?: string;
  /**
   * `performance.now()` value captured by the caller at the moment the
   * refresh round-trip actually started (i.e. immediately before
   * `getToken()` was invoked). The helper is `'client-only'` and is
   * dynamic-imported by the bridge, so without this anchor every
   * timestamp inside this function (span start, duration math, start
   * breadcrumb) gets shifted by the dynamic-import latency — and if
   * `getToken` resolves before the import does, the duration looks like
   * ~0ms despite a real refresh having happened. When omitted, the
   * helper falls back to its own `performance.now()` so callers that
   * invoke this helper synchronously still work.
   */
  startedAt?: number;
}

interface SettleAttemptArgs {
  outcome: ClerkRefreshOutcomeStatus;
  /** Attached as a breadcrumb hint only — never serialised onto a tag. */
  error?: Error;
}

type SettleFn = (args: SettleAttemptArgs) => void;

const NOOP_SETTLE: SettleFn = () => undefined;

function setRootSpanAttribute(key: string, value: string | number): void {
  try {
    const active = Sentry.getActiveSpan();
    if (!active) return;
    Sentry.getRootSpan(active).setAttribute(key, value);
  } catch {
    // Observability must never interrupt the recovery flow.
  }
}

function setScopeTag(key: string, value: string | number | undefined): void {
  try {
    Sentry.getCurrentScope().setTag(key, value);
  } catch {
    // Observability must never interrupt the recovery flow.
  }
}

function addBreadcrumb(message: string, data: Record<string, unknown>): void {
  try {
    Sentry.addBreadcrumb({
      category: BREADCRUMB_CATEGORY,
      message,
      level: 'info',
      data,
    });
  } catch {
    // Observability must never interrupt the recovery flow.
  }
}

/**
 * Begin a `clerk.refresh` span for one in-flight refresh cycle. Returns a
 * settle function that records the outcome, ends the span, and mirrors
 * the outcome onto the active root span so concurrent errors are joinable
 * by the same `clerk_refresh.outcome` tag.
 *
 * Call ONCE per in-flight cycle — the bridge already dedupes concurrent
 * callers to a single FAPI round-trip, and one span per cycle keeps
 * widgets honest (`avg(clerk_refresh.duration_ms)` should reflect the
 * underlying network latency, not the caller fan-out).
 *
 * When Sentry is disabled or any internal call throws, the returned
 * settle function is a no-op.
 */
export function observeClerkRefreshAttempt({
  trigger,
  hiddenDurationMs,
  sourceKey,
  startedAt: callerStartedAt,
}: ObserveAttemptArgs): SettleFn {
  if (!sentryConfig.enabled) return NOOP_SETTLE;
  if (typeof window === 'undefined') return NOOP_SETTLE;

  // Anchor the duration window to the caller's perf-clock reading when
  // provided. The bridge captures `performance.now()` immediately before
  // calling `getToken()` and passes it through `beginObservedRefresh`,
  // which only awaits the dynamic import. Falling back to "now" keeps
  // the helper usable for any future caller that invokes it
  // synchronously.
  const helperNow = performance.now();
  const startedAt = callerStartedAt ?? helperNow;
  // Translate the perf-clock anchor into wall-clock time so Sentry's
  // span starts at the real kickoff moment — not at import resolution.
  // `performance.now()` is monotonic-since-page-load, so the delta from
  // it gives us the elapsed millis since the caller's anchor, which we
  // subtract from `Date.now()` to recover the original wall-clock instant.
  const spanStartTime: Date | undefined =
    callerStartedAt !== undefined
      ? new Date(Date.now() - (helperNow - callerStartedAt))
      : undefined;

  // Stamp the trigger on the root span up front so any errors that fire
  // BEFORE the refresh settles already carry the recovery context.
  setRootSpanAttribute(CLERK_REFRESH_ATTR.TRIGGER, trigger);
  if (typeof hiddenDurationMs === 'number') {
    setRootSpanAttribute(
      CLERK_REFRESH_ATTR.HIDDEN_DURATION_MS,
      hiddenDurationMs
    );
  }
  if (sourceKey) {
    setRootSpanAttribute(CLERK_REFRESH_ATTR.SOURCE_KEY, sourceKey);
  }

  let span: ReturnType<typeof Sentry.startInactiveSpan> | undefined;
  try {
    span = Sentry.startInactiveSpan({
      name: SPAN_NAME,
      op: SPAN_OP,
      ...(spanStartTime ? { startTime: spanStartTime } : {}),
      attributes: {
        [CLERK_REFRESH_ATTR.TRIGGER]: trigger,
        ...(typeof hiddenDurationMs === 'number'
          ? { [CLERK_REFRESH_ATTR.HIDDEN_DURATION_MS]: hiddenDurationMs }
          : {}),
        ...(sourceKey ? { [CLERK_REFRESH_ATTR.SOURCE_KEY]: sourceKey } : {}),
      },
    });
  } catch {
    // Span creation must never break the refresh. Fall through with
    // span === undefined; the settle function still records breadcrumbs
    // and root-span attributes.
  }

  addBreadcrumb('clerk.refresh.start', {
    trigger,
    ...(typeof hiddenDurationMs === 'number' ? { hiddenDurationMs } : {}),
    ...(sourceKey ? { sourceKey } : {}),
  });

  return ({ outcome, error }) => {
    const durationMs = Math.round(performance.now() - startedAt);
    try {
      span?.setAttribute(CLERK_REFRESH_ATTR.OUTCOME, outcome);
      span?.setAttribute(CLERK_REFRESH_ATTR.DURATION_MS, durationMs);
      span?.end();
    } catch {
      // Span end must never break the refresh.
    }

    // Mirror outcome onto the root span so errors fired during the
    // recovery window can be filtered by outcome too.
    setRootSpanAttribute(CLERK_REFRESH_ATTR.OUTCOME, outcome);
    setRootSpanAttribute(CLERK_REFRESH_ATTR.DURATION_MS, durationMs);

    addBreadcrumb('clerk.refresh.settle', {
      trigger,
      outcome,
      durationMs,
      ...(typeof hiddenDurationMs === 'number' ? { hiddenDurationMs } : {}),
      ...(sourceKey ? { sourceKey } : {}),
      ...(error ? { error: error.message } : {}),
    });
  };
}

/**
 * Record the visibility-handler's consecutive non-success streak so the
 * dashboard can group "which sessions are getting close to the
 * hard-reload threshold" without waiting for the reload itself to fire.
 *
 * Set on every visibility-handler cycle (including resets to 0 after a
 * `refreshed` outcome) so the latest streak value always reflects reality.
 */
export function observeClerkRefreshStreak(consecutiveFailures: number): void {
  if (!sentryConfig.enabled) return;
  if (typeof window === 'undefined') return;

  setRootSpanAttribute(
    CLERK_REFRESH_ATTR.CONSECUTIVE_FAILURES,
    consecutiveFailures
  );
  setScopeTag(CLERK_REFRESH_ATTR.CONSECUTIVE_FAILURES, consecutiveFailures);
}

/**
 * Capture a first-class searchable event the moment the visibility hook
 * decides to hard-reload the page. Without this, hard reloads are
 * invisible in Sentry — the post-reload load looks like any other
 * pageview.
 */
export function observeClerkRefreshHardReload({
  consecutiveFailures,
  trigger,
}: {
  consecutiveFailures: number;
  trigger: ClerkRefreshTrigger;
}): void {
  if (!sentryConfig.enabled) return;
  if (typeof window === 'undefined') return;

  try {
    Sentry.withScope(scope => {
      scope.setTag(CLERK_REFRESH_ATTR.TRIGGER, trigger);
      scope.setTag(
        CLERK_REFRESH_ATTR.CONSECUTIVE_FAILURES,
        consecutiveFailures
      );
      scope.setLevel('warning');
      Sentry.captureMessage(CLERK_REFRESH_MESSAGE.HARD_RELOAD);
    });
  } catch {
    // Observability must never interrupt the recovery flow.
  }
}

/**
 * Record that the user actually saw the "Reconnecting…" or "Reconnection
 * failed" pill (after the indicator hook's 750 ms debounce). The two
 * states carry different signal: a slow-but-successful reconnect is a
 * latency metric, while a failure is something a human should see.
 *
 * - `refreshing` is the common slow-reconnect case — a breadcrumb plus a
 *   root-span attribute keep it queryable on dashboards without minting a
 *   Sentry Issue for every spinner.
 * - `failed` is a real problem, so it stays a first-class captured Issue
 *   at `warning` level.
 */
export function observeClerkRefreshIndicatorShown(
  state: ClerkRefreshIndicatorVisibleState
): void {
  if (!sentryConfig.enabled) return;
  if (typeof window === 'undefined') return;

  if (state === 'refreshing') {
    setRootSpanAttribute(CLERK_REFRESH_ATTR.INDICATOR_STATE, state);
    addBreadcrumb(CLERK_REFRESH_MESSAGE.INDICATOR_SHOWN, { state });
    return;
  }

  try {
    Sentry.withScope(scope => {
      scope.setTag(CLERK_REFRESH_ATTR.INDICATOR_STATE, state);
      scope.setLevel('warning');
      Sentry.captureMessage(CLERK_REFRESH_MESSAGE.INDICATOR_SHOWN);
    });
  } catch {
    // Observability must never interrupt the recovery flow.
  }
}
